import type {UploadFileDataTypes, UserMediaVOResponse} from '@Libraries/user-media-library';
import {getMaxAllowedUploadFileSize, getReadableMaxAllowedUploadFileSize, MultiMediaUploadSource, UPLOAD_LOADING_KEY, uploadFile, UserMediaType} from '@Libraries/user-media-library';
import {RENDERER_POLL_SLEEP_CADENCE_FORM_FIELD, RendererPollSleepCadence} from '@Libraries/renderer-poll-sleep-cadence';
import {isTempImageData} from '@Libraries/add-media-library';
import type {UserImageAttachment} from '@Components/ai-prompt-box/components/ai-prompt-box-attachments/ai-prompt-box-attachments.types';
import {PromptAttachmentType} from '@Components/ai-prompt-box/components/ai-prompt-box-attachments/ai-prompt-box-attachments.types';
import {convertUserMediaVOToAttachment, createPendingImageAttachment, MAX_ATTACHMENTS, validatePresetsInBackground} from '@Components/ai-prompt-box/libraries/ai-prompt-box-attachment.library';
import {clearPendingUpload, getPendingUploadFile, registerPendingUpload, updatePendingUploadPromise} from '@Components/ai-prompt-box/libraries/ai-prompt-box-pending-uploads.library';
import {GROWL_TYPE, showMessageGrowl} from '@Components/message-growl';
import isArray from 'lodash/isArray';

const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export const AI_PROMPT_BOX_ACCEPTED_IMAGE_TYPES = ACCEPTED_IMAGE_TYPES;

export interface DragDropUploadState {
  promptBoxId: string;
  getAttachmentCount: () => number;
  setActive: () => void;
  removeImageAttachments: (hashedFilenames: string[]) => void;
  addPendingImageAttachments: (attachments: UserImageAttachment[]) => void;
  resolvePendingAttachment: (localId: string, resolved: UserImageAttachment) => void;
  markPendingAttachmentFailed: (localId: string) => void;
  markPendingAttachmentUploading: (localId: string) => void;
}

interface UploadableFileItem {
  file: File;
  localId: string;
  resolveFn: (response: UserMediaVOResponse) => void;
  rejectFn: () => void;
}

interface PendingFileItem extends UploadableFileItem {
  attachment: UserImageAttachment;
  blobUrl: string;
  promise: Promise<UserMediaVOResponse>;
}

interface DeferredUpload {
  promise: Promise<UserMediaVOResponse>;
  resolveFn: (response: UserMediaVOResponse) => void;
  rejectFn: () => void;
}

const noop = (): void => {
  /* failures are surfaced via state.markPendingAttachmentFailed; awaitAll uses Promise.allSettled */
};

/**
 * The pending promise stored in the registry is only awaited later, when the user submits.
 * Until then we attach a no-op rejection handler so a failed upload doesn't surface as an
 * "Uncaught (in promise)" warning while the user is still deciding what to do.
 */
const createDeferredUpload = (): DeferredUpload => {
  let resolveFn!: (response: UserMediaVOResponse) => void;
  let rejectFn!: () => void;
  const promise = new Promise<UserMediaVOResponse>((resolve, reject) => {
    resolveFn = resolve;
    rejectFn = reject;
  });
  promise.catch(noop);
  return {promise, resolveFn, rejectFn};
};

interface FilteredFiles {
  accepted: File[];
  unsupported: File[];
  oversized: File[];
}

export const handleDragDropFilesUpload = (files: File[], state: DragDropUploadState, onInvalidFileTypeError?: VoidFunction): void => {
  const currentCount = state.getAttachmentCount();
  if (currentCount >= MAX_ATTACHMENTS) {
    return;
  }

  const filtered = filterDroppedFiles(files);
  surfaceFilteringErrors(filtered, onInvalidFileTypeError);

  if (filtered.accepted.length === 0) {
    return;
  }

  const availableSlots = MAX_ATTACHMENTS - currentCount;
  const filesToUpload = filtered.accepted.slice(0, availableSlots);

  const pendingItems = filesToUpload.map(buildPendingItem);

  state.addPendingImageAttachments(pendingItems.map((item) => item.attachment));
  pendingItems.forEach((item) => {
    registerPendingUpload(state.promptBoxId, item.localId, item.blobUrl, item.promise, item.file);
  });
  state.setActive();

  void runUploadBatch(pendingItems, state);
};

export const retryPendingUpload = (localId: string, state: DragDropUploadState): void => {
  const file = getPendingUploadFile(state.promptBoxId, localId);
  if (!file) {
    return;
  }

  const {promise, resolveFn, rejectFn} = createDeferredUpload();
  updatePendingUploadPromise(state.promptBoxId, localId, promise);
  state.markPendingAttachmentUploading(localId);

  void uploadSingleAiChatFile({file, localId, resolveFn, rejectFn}, state);
};

const filterDroppedFiles = (files: File[]): FilteredFiles => {
  const maxSize = getMaxAllowedUploadFileSize();
  const result: FilteredFiles = {accepted: [], unsupported: [], oversized: []};

  files.forEach((file) => {
    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      result.unsupported.push(file);
      return;
    }
    if (file.size > maxSize) {
      result.oversized.push(file);
      return;
    }
    result.accepted.push(file);
  });

  return result;
};

const surfaceFilteringErrors = (filtered: FilteredFiles, onInvalidFileTypeError?: VoidFunction): void => {
  if (filtered.unsupported.length > 0) {
    onInvalidFileTypeError?.();
  }

  if (filtered.oversized.length > 0) {
    showOversizedFileGrowl(filtered.oversized.length);
  }
};

const showOversizedFileGrowl = (count: number): void => {
  const text =
    count === 1
      ? window.i18next.t('pmwjs_upload_some_media_file_exceeding_limit', {uploadFileSizeLimit: getReadableMaxAllowedUploadFileSize()})
      : window.i18next.t('pmwjs_upload_some_media_files_exceeding_limit', {numfiles: count, uploadFileSizeLimit: getReadableMaxAllowedUploadFileSize()});

  showMessageGrowl({
    type: GROWL_TYPE.DANGER,
    text,
    showCloseIcon: true,
    interval: 7000,
  });
};

const buildPendingItem = (file: File): PendingFileItem => {
  const {attachment, localId, blobUrl} = createPendingImageAttachment(file);
  const {promise, resolveFn, rejectFn} = createDeferredUpload();
  return {file, attachment, localId, blobUrl, resolveFn, rejectFn, promise};
};

const runUploadBatch = async (items: PendingFileItem[], state: DragDropUploadState): Promise<void> => {
  await Promise.allSettled(items.map((item) => uploadSingleAiChatFile(item, state)));
};

const uploadSingleAiChatFile = async (item: UploadableFileItem, state: DragDropUploadState): Promise<void> => {
  const fd = new FormData();
  fd.append('Filedata', item.file);
  fd.append('type', UserMediaType.IMAGE);
  fd.append('mirror', 'false');
  fd.append(RENDERER_POLL_SLEEP_CADENCE_FORM_FIELD, RendererPollSleepCadence.FAST);

  try {
    const data = (await uploadFile(fd, UPLOAD_LOADING_KEY, UserMediaType.IMAGE, MultiMediaUploadSource.UPLOAD)) as UploadFileDataTypes;
    handleSingleFileUpload(data, item, state);
  } catch {
    handleSingleFileError(item, state);
  }
};

const handleSingleFileUpload = (data: UploadFileDataTypes, item: UploadableFileItem | undefined, state: DragDropUploadState): void => {
  const responses = extractNonTempResponses(data);
  if (!item || responses.length === 0) {
    return;
  }

  const response = responses[0];
  const resolved = convertUserMediaVOToAttachment(response, PromptAttachmentType.IMAGE);
  state.resolvePendingAttachment(item.localId, resolved);
  clearPendingUpload(state.promptBoxId, item.localId);
  item.resolveFn(response);

  if (resolved.hashedFilename) {
    void validatePresetsInBackground([resolved.hashedFilename], state.removeImageAttachments);
  }
};

const handleSingleFileError = (item: UploadableFileItem | undefined, state: DragDropUploadState): void => {
  if (!item) {
    return;
  }

  state.markPendingAttachmentFailed(item.localId);
  item.rejectFn();
};

const extractNonTempResponses = (data: UploadFileDataTypes): UserMediaVOResponse[] => {
  if (isArray(data)) {
    return data.filter((item): item is UserMediaVOResponse => !isTempImageData(item));
  }

  if (!isTempImageData(data)) {
    return [data];
  }

  return [];
};
